import React, { useState, useEffect } from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Image, Platform } from 'react-native'
import { Button, Text, Spinner, Icon, Toggle, Card, Modal, OverflowMenu, MenuItem, useTheme } from '@ui-kitten/components'
import * as ImagePicker from 'expo-image-picker'
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator'
import StyledTextInput from '../../components/StyledTextInput'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue, useAtom } from 'jotai'
import { printTemplatesAtom, storesDictAtom } from '../../global/AtomsProvider'
import { savePrintTemplate, deletePrintTemplate, getPrintTemplates, uploadPrintTemplateImage, storage } from '../../global/Firebase/firebase'

const FIELD_OPTIONS = [
  { id: 'invoiceNumber' },
  { id: 'date' },
  { id: 'supplier' },
  { id: 'buyer' },
  { id: 'clientPhone' },
  { id: 'clientAddress' },
  { id: 'itemsTable' },
  { id: 'productImages' },
  { id: 'totalAmount' },
  { id: 'amountInWords' }
]

const PAGE_SIZE_OPTIONS = [
  { id: 'A4', text: 'A4 (210mm)', width: 210 },
  { id: 'receipt', text: 'Receipt (58mm)', width: 58 },
  { id: 'custom', text: 'Custom' }
]


export default function PrintSettingsScreen({ navigation }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const storesDict = useAtomValue(storesDictAtom)
  const [printTemplates, setPrintTemplates] = useAtom(printTemplatesAtom)
  
  // Default settings
  const defaultSettings = {
    title: t('settings.invoiceTitle'),
    subtitle: t('settings.invoiceSubtitle'),
    includeFields: {
      invoiceNumber: true,
      date: true,
      supplier: true,
      buyer: true,
      clientPhone: false,
      clientAddress: false,
      itemsTable: true,
      productImages: false,
      totalAmount: true,
      amountInWords: true
    },
    includeVAT: false,
    vatRate: 12,
    additionalText: '',
    images: {
      logo: { enabled: false, fileName: null, url: null },
      extra: { enabled: false, fileName: null, url: null, widthPx: 80 }
    },
    signatureFields: {
      released: { enabled: true, label: t('invoice.released') },
      received: { enabled: true, label: t('invoice.received') }
    },
    pageSettings: {
      width: 'A4',
      customWidth: 210,
      padding: 10
    }
  }

  // State for template management
  const [selectedStore, setSelectedStore] = useState('all')
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState(null)

  // State for template form
  const [templateName, setTemplateName] = useState('')
  const [templateType, setTemplateType] = useState('sale')
  const [templateStoreId, setTemplateStoreId] = useState('all')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [includeFields, setIncludeFields] = useState({})
  const [includeVAT, setIncludeVAT] = useState(false)
  const [vatRate, setVatRate] = useState('12')
  const [additionalText, setAdditionalText] = useState('')
  const [logoEnabled, setLogoEnabled] = useState(false)
  const [logoImage, setLogoImage] = useState({ fileName: null, localUri: null })
  const [extraEnabled, setExtraEnabled] = useState(false)
  const [extraImage, setExtraImage] = useState({ fileName: null, localUri: null })
  const [extraWidth, setExtraWidth] = useState('80')
  const [imageUploading, setImageUploading] = useState(false)
  const [imagePickerMenuVisible, setImagePickerVisible] = useState({ key: null, visible: false })
  const [signatureFields, setSignatureFields] = useState({ released: { enabled: true, label: t('invoice.released') }, received: { enabled: true, label: t('invoice.received') } })
  const [pageWidth, setPageWidth] = useState('A4')
  const [customWidth, setCustomWidth] = useState('210')
  const [pagePadding, setPagePadding] = useState('10')
  
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={openCreateModal}
          iconName='plus-outline'
        />
      ),
    })
  }, [])

  useEffect(() => {
    loadTemplates()
  }, [])

  useEffect(() => {
    // Filter templates whenever printTemplates or selectedStore changes
    let filtered = printTemplates.filter(template => template.type === 'sale')

    if (selectedStore !== 'all') {
      filtered = filtered.filter(template =>
        template.store === selectedStore || !template.store
      )
    }

    setTemplates(filtered)
  }, [selectedStore, printTemplates])

  const loadTemplates = async () => {
    try {
      const templatesData = await getPrintTemplates()
      setPrintTemplates(templatesData)
    } catch (error) {
      console.error('Error loading templates:', error)
      Sentry.captureException(error)
    }
  }

  const resetForm = () => {
    setTemplateName('')
    setTemplateType('sale')
    setTemplateStoreId('all')
    setTitle(defaultSettings.title)
    setSubtitle(defaultSettings.subtitle)
    setIncludeFields(defaultSettings.includeFields)
    setIncludeVAT(defaultSettings.includeVAT)
    setVatRate(defaultSettings.vatRate.toString())
    setAdditionalText(defaultSettings.additionalText)
    setLogoEnabled(defaultSettings.images.logo.enabled)
    setLogoImage({ fileName: defaultSettings.images.logo.fileName, localUri: defaultSettings.images.logo.url })
    setExtraEnabled(defaultSettings.images.extra.enabled)
    setExtraImage({ fileName: defaultSettings.images.extra.fileName, localUri: defaultSettings.images.extra.url })
    setExtraWidth(defaultSettings.images.extra.widthPx.toString())
    setSignatureFields(defaultSettings.signatureFields)
    setPageWidth(defaultSettings.pageSettings.width)
    setCustomWidth(defaultSettings.pageSettings.customWidth.toString())
    setPagePadding(defaultSettings.pageSettings.padding.toString())
    setSaveError('')
  }

  const openCreateModal = () => {
    resetForm()
    setIsCreating(true)
    setIsEditing(false)
    setShowTemplateModal(true)
  }

  const openEditModal = (template) => {
    const settings = template.settings || {}
    setTemplateName(template.name)
    setTemplateType('sale')
    setTemplateStoreId(template.store || 'all')
    setTitle(settings.title || defaultSettings.title)
    setSubtitle(settings.subtitle || defaultSettings.subtitle)
    setIncludeFields(settings.includeFields || defaultSettings.includeFields)
    setIncludeVAT(settings.includeVAT || defaultSettings.includeVAT)
    setVatRate(settings.vatRate?.toString() || '12')
    setAdditionalText(settings.additionalText || '')
    setLogoEnabled(settings.images?.logo?.enabled || false)
    setLogoImage({ fileName: settings.images?.logo?.fileName || null, localUri: settings.images?.logo?.url || null })
    setExtraEnabled(settings.images?.extra?.enabled || false)
    setExtraImage({ fileName: settings.images?.extra?.fileName || null, localUri: settings.images?.extra?.url || null })
    setExtraWidth((settings.images?.extra?.widthPx || 80).toString())
    setSignatureFields(settings.signatureFields || defaultSettings.signatureFields)
    setPageWidth(settings.pageSettings?.width || defaultSettings.pageSettings.width)
    setCustomWidth((settings.pageSettings?.customWidth || defaultSettings.pageSettings.customWidth).toString())
    setPagePadding((settings.pageSettings?.padding || defaultSettings.pageSettings.padding).toString())
    setSelectedTemplate(template)
    setIsCreating(false)
    setIsEditing(true)
    setShowTemplateModal(true)
  }

  const closeModal = () => {
    setShowTemplateModal(false)
    setSelectedTemplate(null)
    setIsCreating(false)
    setIsEditing(false)
    resetForm()
  }

  const updateField = (fieldId, value) => {
    setIncludeFields(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const updateSignatureFieldEnabled = (fieldKey, value) => {
    setSignatureFields(prev => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], enabled: value }
    }))
  }

  const updateSignatureFieldLabel = (fieldKey, value) => {
    setSignatureFields(prev => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], label: value }
    }))
  }

  const pickAndUploadImage = async (key) => {
    setImageUploading(true)
    
    if (key === 'camera') {
      let permissionResult = await ImagePicker.requestCameraPermissionsAsync()
      if (permissionResult.granted === false) {
        alert('Permission required')
        setImageUploading(false)
        return
      }
    }
    
    const picker = key === 'camera' ? ImagePicker.launchCameraAsync : ImagePicker.launchImageLibraryAsync
    const pickerOptions = key === 'camera' 
      ? { mediaTypes: ImagePicker.MediaTypeOptions.Images }
      : { 
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsEditing: false,
          quality: 1
        }
    
    const pickerResult = await picker(pickerOptions)
    if (pickerResult.canceled) { setImageUploading(false); return }
    const result = await manipulateAsync(pickerResult.assets[0].uri, [{ resize: { width: 1200 } }], { compress: 0.6, format: SaveFormat.JPEG })
    const fileName = (templateName || 'template') + '_' + (new Date().getTime())
    try {
      if (Platform.OS === 'web') {
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.onload = function() { resolve(xhr.response) }
          xhr.onerror = function(_e) { reject(new TypeError('Network request failed')) }
          xhr.responseType = 'blob'
          xhr.open('GET', result.uri, true)
          xhr.send(null)
        })
        await uploadPrintTemplateImage(blob, fileName)
      } else {
        await uploadPrintTemplateImage(result.uri, fileName)
      }
      const url = await storage.child('printTemplates/' + fileName).getDownloadURL()
      return { fileName, localUri: url }
    } catch(_e) {
      Sentry.captureException(_e)
      return null
    } finally {
      setImageUploading(false)
      setImagePickerVisible({ key: null, visible: false })
    }
  }

  const onUploadLogo = async (source) => {
    const uploaded = await pickAndUploadImage(source)
    if (uploaded) setLogoImage(uploaded)
  }
  const onUploadExtra = async (source) => {
    const uploaded = await pickAndUploadImage(source)
    if (uploaded) setExtraImage(uploaded)
  }

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      setSaveError(t('settings.templateName') + ' ' + t('fieldIsRequiredError'))
      return
    }

    setIsLoading(true)
    setSaveError('')
    
    const settings = {
      title,
      subtitle,
      includeFields,
      includeVAT,
      vatRate: parseFloat(vatRate) || 0,
      additionalText: additionalText || '',
      images: {
        logo: { enabled: logoEnabled, fileName: logoImage.fileName || null, url: logoImage.localUri || null },
        extra: { enabled: extraEnabled, fileName: extraImage.fileName || null, url: extraImage.localUri || null, widthPx: parseInt(extraWidth) || 80 }
      },
      signatureFields,
      pageSettings: {
        width: pageWidth,
        customWidth: parseInt(customWidth) || 210,
        padding: parseInt(pagePadding) || 10
      }
    }

    const templateData = {
      name: templateName.trim(),
      type: templateType,
      store: templateStoreId === 'all' ? null : templateStoreId,
      settings,
      isDefault: false
    }

    if (isEditing && selectedTemplate) {
      templateData.id = selectedTemplate.id
    }

    try {
      await savePrintTemplate(templateData)

      // Reload templates to get consistent data format from API
      await loadTemplates()

      setSuccessMessageVisible(true)
      setIsLoading(false)
      closeModal()
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  const confirmDeleteTemplate = (template) => {
    setTemplateToDelete(template)
    setShowDeleteModal(true)
  }

  const deleteTemplate = async () => {
    if (!templateToDelete) return

    try {
      await deletePrintTemplate(templateToDelete.id)

      // Reload templates to get consistent data format from API
      await loadTemplates()

      setShowDeleteModal(false)
      setTemplateToDelete(null)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
    }
  }

  

  // removed image position options

  const localizedFieldOptions = FIELD_OPTIONS.map(option => ({
    ...option,
    text: t(`settings.fieldOptions.${option.id}`)
  }))

  const storeOptions = [
    { id: 'all', text: t('settings.globalTemplate') },
    ...Object.values(storesDict).map(store => ({
      id: store.id,
      text: store.name
    }))
  ]


  const renderTemplateCard = (template) => (
    <Card key={template.id} style={styles.templateCard} onPress={() => openEditModal(template)}>
      <View style={styles.templateHeader}>
        <View style={styles.templateInfo}>
          <Text category="h6" style={styles.templateName}>
            {template.name}
            {template.isDefault && (
              <Text category="c1" status="primary" style={styles.defaultBadge}>
                {' '}({t('settings.defaultTemplate')})
              </Text>
            )}
          </Text>
          <Text category="c1" style={[styles.templateType, {color: theme['text-hint-color']}]}>
            {t(`settings.templateTypes.${template.type}`)} • {template.store 
              ? t('settings.templateForStore', { storeName: storesDict[template.store]?.name || template.store })
              : t('settings.globalTemplate')
            }
          </Text>
        </View>
        <View />
      </View>
    </Card>
  )

  return (
    <KeyboardAvoidingView style={styles.keyboardView} behavior="height" keyboardVerticalOffset={100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <StoreChooserHorizontal 
          store={selectedStore} 
          onChange={setSelectedStore}
          showAll={true}
        />

        {templates.length === 0 ? (
          <Card style={styles.emptyState}>
            <Text category="h6" style={styles.emptyTitle}>
              {t('settings.noTemplates')}
            </Text>
            <Text category="p2" style={[styles.emptyDescription, {color: theme['text-hint-color']}]}>
              {t('settings.createFirstTemplate')}
            </Text>
          </Card>
        ) : (
          templates.map(renderTemplateCard)
        )}

        {/* Template Modal */}
        <Modal
          visible={showTemplateModal}
          style={[styles.modal, {backgroundColor: theme['background-basic-color-1']}]} 
          backdropStyle={styles.backdrop}
          onBackdropPress={closeModal}
        >
            <ScrollView style={{padding: 20}}>
              <Text category='h6' style={styles.modalTitle}>
                {isCreating ? t('settings.createTemplate') : t('settings.editTemplate')}
              </Text>
              <Text category='label' style={styles.label}>
                {t('settings.templateName')}
              </Text>
              <StyledTextInput
                value={templateName}
                onChangeText={setTemplateName}
                placeholder={t('settings.templateName')}
                returnKeyType='done'
                autoCapitalize='sentences'
              />


              <Text category='label' style={styles.label}>
                {t('settings.selectStore')}
              </Text>
              <PlatformDependantSelect
                data={storeOptions}
                selectedOption={storeOptions.find(opt => opt.id === templateStoreId)}
                onSelect={(option) => setTemplateStoreId(option.id)}
                placeholder={t('settings.selectStore')}
              />

              <Text category='label' style={styles.label}>
                {t('settings.invoiceTitle')}
              </Text>
              <StyledTextInput
                value={title}
                onChangeText={setTitle}
                placeholder={t('settings.invoiceTitle')}
                returnKeyType='done'
                autoCapitalize='sentences'
              />

              <Text category='label' style={styles.label}>
                {t('settings.invoiceSubtitle')}
              </Text>
              <StyledTextInput
                value={subtitle}
                onChangeText={setSubtitle}
                placeholder={t('settings.invoiceSubtitle')}
                returnKeyType='done'
                autoCapitalize='sentences'
              />

              <Text category='label' style={styles.label}>
                {t('settings.pageSize')}
              </Text>
              <PlatformDependantSelect
                data={PAGE_SIZE_OPTIONS}
                selectedOption={PAGE_SIZE_OPTIONS.find(opt => opt.id === pageWidth)}
                onSelect={(option) => setPageWidth(option.id)}
                placeholder={t('settings.selectPageSize')}
              />

              {pageWidth === 'custom' && (
                <>
                  <Text category='label' style={styles.label}>
                    {t('settings.customWidth')}
                  </Text>
                  <StyledTextInput
                    value={customWidth}
                    onChangeText={setCustomWidth}
                    placeholder={t('settings.customWidth')}
                    keyboardType='numeric'
                    returnKeyType='done'
                  />
                </>
              )}

              <Text category='label' style={styles.label}>
                {t('settings.pagePadding')}
              </Text>
              <StyledTextInput
                value={pagePadding}
                onChangeText={setPagePadding}
                placeholder={t('settings.pagePadding')}
                keyboardType='numeric'
                returnKeyType='done'
              />

              <Text category='label' style={styles.label}>{t('settings.imagesSection')}</Text>
              <Toggle
                checked={logoEnabled}
                onChange={setLogoEnabled}
                style={styles.toggle}
              >
                {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.logoImage')}</Text>}
              </Toggle>
              {logoEnabled && (
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  {imageUploading
                    ? (
                      <View style={styles.spinnerView}>
                        <Spinner />
                      </View>
                    ) : (
                      <OverflowMenu
                        visible={imagePickerMenuVisible.key === 'logo' && imagePickerMenuVisible.visible}
                        placement='right'
                        selectedIndex={null}
                        backdropStyle={styles.backdrop}
                        onSelect={(selectedIndex) => selectedIndex.row === 0 ? onUploadLogo('camera') : onUploadLogo('library')}
                        onBackdropPress={() => setImagePickerVisible({ key: null, visible: false })}
                        anchor={() => (
                          <Button
                            disabled={imageUploading}
                            onPress={() => setImagePickerVisible({ key: 'logo', visible: true })}
                            style={styles.addButton}
                            status='basic'
                            accessoryLeft={(style) => (
                              <Icon {...style} fill={theme['text-hint-color']} name='edit-outline'/>
                            )} 
                          />
                        )}
                      >
                        <MenuItem title={t('supplies.takePhoto')} />
                        <MenuItem title={t('supplies.choosePhoto')} />
                      </OverflowMenu>
                    )
                  }
                  {(logoImage.localUri) && (
                    <Image source={{ uri: logoImage.localUri }} style={{width: 60, height: 60, marginLeft: 10}} />
                  )}
                </View>
              )}

              <Toggle
                checked={extraEnabled}
                onChange={setExtraEnabled}
                style={styles.toggle}
              >
                {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.additionalImage')}</Text>}
              </Toggle>
              {extraEnabled && (
                <View>
                  <View style={{flexDirection: 'row', alignItems: 'center'}}>
                    {imageUploading
                      ? (
                        <View style={styles.spinnerView}>
                          <Spinner />
                        </View>
                      ) : (
                        <OverflowMenu
                          visible={imagePickerMenuVisible.key === 'extra' && imagePickerMenuVisible.visible}
                          placement='right'
                          selectedIndex={null}
                          backdropStyle={styles.backdrop}
                          onSelect={(selectedIndex) => selectedIndex.row === 0 ? onUploadExtra('camera') : onUploadExtra('library')}
                          onBackdropPress={() => setImagePickerVisible({ key: null, visible: false })}
                          anchor={() => (
                            <Button
                              disabled={imageUploading}
                              onPress={() => setImagePickerVisible({ key: 'extra', visible: true })}
                              style={styles.addButton}
                              status='basic'
                              accessoryLeft={(style) => (
                                imageUploading ? <Spinner size='tiny' /> : <Icon {...style} fill={theme['text-hint-color']} name='edit-outline'/>
                              )}
                            />
                          )}
                        >
                          <MenuItem title={t('supplies.takePhoto')} />
                          <MenuItem title={t('supplies.choosePhoto')} />
                        </OverflowMenu>
                      )
                    }
                    {(extraImage.localUri) && (
                      <Image source={{ uri: extraImage.localUri }} style={{width: 60, height: 60, marginLeft: 10}} />
                    )}
                  </View>
                  <Text category='label' style={styles.label}>{t('settings.additionalImageWidth')}</Text>
                  <StyledTextInput
                    value={extraWidth}
                    onChangeText={setExtraWidth}
                    placeholder={t('settings.additionalImageWidth')}
                    keyboardType='numeric'
                    returnKeyType='done'
                  />
                </View>
              )}

              <Text category='label' style={styles.label}>
                {t('settings.includeFields')}
              </Text>
              {localizedFieldOptions.map((option) => (
                <Toggle
                  key={option.id}
                  checked={includeFields[option.id]}
                  onChange={(checked) => updateField(option.id, checked)}
                  style={styles.toggle}
                >
                  {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{option.text}</Text>}
                </Toggle>
              ))}

              <Toggle
                checked={includeVAT}
                onChange={setIncludeVAT}
                style={styles.toggle}
              >
                {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('settings.includeVAT')}</Text>}
              </Toggle>
              {includeVAT && (
                <>
                  <Text category='label' style={styles.label}>
                    {t('settings.vatRate')}
                  </Text>
                  <StyledTextInput
                    value={vatRate}
                    onChangeText={setVatRate}
                    placeholder={t('settings.vatRate')}
                    keyboardType="numeric"
                    returnKeyType='done'
                  />
                </>
              )}

              <Text category='label' style={styles.label}>
                {t('settings.signatureFields')}
              </Text>
              <Toggle
                checked={signatureFields?.released?.enabled}
                onChange={(checked) => updateSignatureFieldEnabled('released', checked)}
                style={styles.toggle}
              >
                {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('invoice.released')}</Text>}
              </Toggle>
              {signatureFields?.released?.enabled && (
                <StyledTextInput
                  value={signatureFields?.released?.label}
                  onChangeText={(value) => updateSignatureFieldLabel('released', value)}
                  placeholder={t('invoice.released')}
                  returnKeyType='done'
                  style={styles.signatureField}
                />
              )}

              <Toggle
                checked={signatureFields?.received?.enabled}
                onChange={(checked) => updateSignatureFieldEnabled('received', checked)}
                style={styles.toggle}
              >
                {evaProps => <Text {...evaProps} style={[evaProps.style, styles.toggleText]}>{t('invoice.received')}</Text>}
              </Toggle>
              {signatureFields?.received?.enabled && (
                <StyledTextInput
                  value={signatureFields?.received?.label}
                  onChangeText={(value) => updateSignatureFieldLabel('received', value)}
                  placeholder={t('invoice.received')}
                  returnKeyType='done'
                  style={styles.signatureField}
                />
              )}

              <Text category='label' style={styles.label}>{t('settings.additionalText')}</Text>
              <StyledTextInput
                value={additionalText}
                onChangeText={setAdditionalText}
                placeholder={t('settings.additionalText')}
                returnKeyType='done'
                multiline
              />

              {saveError !== '' && (
                <Text category='p1' status='danger' style={styles.label}>
                  {saveError}
                </Text>
              )}

              <View style={styles.modalActions}>
                {!isCreating && (
                  <Button
                    status="danger"
                    appearance="ghost"
                    onPress={() => {
                      setShowTemplateModal(false)
                      confirmDeleteTemplate(selectedTemplate)
                    }}
                    style={styles.modalButton}
                  >
                    {t('delete')}
                  </Button>
                )}
                <Button
                  appearance="ghost"
                  onPress={closeModal}
                  style={styles.modalButton}
                >
                  {t('cancel')}
                </Button>
                {isLoading ? (
                  <View style={styles.spinner}><Spinner /></View>
                ) : (
                  <Button
                    onPress={saveTemplate}
                    style={styles.modalButton}
                  >
                    {t('save')}
                  </Button>
                )}
              </View>
              <View style={{height: 100}}></View>
            </ScrollView>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          visible={showDeleteModal}
          backdropStyle={styles.backdrop}
          onBackdropPress={() => setShowDeleteModal(false)}
        >
          <Card style={styles.modalCard}>
            <Text category='h6' style={styles.modalTitle}>
              {t('settings.deleteTemplate')}
            </Text>
            <Text category='p1' style={styles.deleteMessage}>
              {t('settings.confirmDeleteTemplate', { templateName: templateToDelete?.name })}
            </Text>
            <View style={styles.modalActions}>
              <Button
                appearance="ghost"
                onPress={() => setShowDeleteModal(false)}
                style={styles.modalButton}
              >
                {t('cancel')}
              </Button>
              <Button
                status="danger"
                onPress={deleteTemplate}
                style={styles.modalButton}
              >
                {t('delete')}
              </Button>
            </View>
          </Card>
        </Modal>

        <View style={styles.bottomSpacer}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: 20,
  },
  templateCard: {
    marginBottom: 12,
  },
  templateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  templateInfo: {
    flex: 1,
    marginRight: 12,
  },
  templateName: {
    marginBottom: 4,
  },
  defaultBadge: {
    fontWeight: 'bold',
  },
  templateType: {
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  emptyTitle: {
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyDescription: {
    textAlign: 'center',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    borderRadius: 20,
  },
  modalTitle: {
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  modalButton: {
    marginTop: 8,
  },
  deleteMessage: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  toggle: {
    marginBottom: 5,
    alignSelf: 'flex-start'
  },
  toggleText: {
   flexShrink: 1
  },
  addButton: {
    marginLeft: 8,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  bottomSpacer: {
    height: 100,
  },
  signatureField: {
    marginBottom: 5,
  }
})